import React from 'react';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';

interface DocumentUploadConfigurationProps {
  maxFileSize: number;
  allowedFileTypes: string[];
  onMaxFileSizeChange: (size: number) => void;
  onAllowedFileTypesChange: (types: string[]) => void;
  disabled?: boolean;
}

const FILE_TYPE_OPTIONS = [
  { value: '.pdf', label: 'PDF (.pdf)' },
  { value: '.doc', label: 'Word Document (.doc)' },
  { value: '.docx', label: 'Word Document (.docx)' },
  { value: '.txt', label: 'Text File (.txt)' },
  { value: '.csv', label: 'CSV File (.csv)' },
  { value: '.jpg', label: 'JPEG Image (.jpg)' },
  { value: '.jpeg', label: 'JPEG Image (.jpeg)' },
  { value: '.png', label: 'PNG Image (.png)' },
  { value: '.gif', label: 'GIF Image (.gif)' },
  { value: '.webp', label: 'WebP Image (.webp)' }
];

const MAX_FILE_SIZE_MB = 100;
const BYTES_PER_MB = 1024 * 1024;

function DocumentUploadConfiguration({
  maxFileSize,
  allowedFileTypes,
  onMaxFileSizeChange,
  onAllowedFileTypesChange,
  disabled = false
}: Readonly<DocumentUploadConfigurationProps>): React.ReactElement {
  const maxFileSizeMB = maxFileSize
    ? Math.round(maxFileSize / BYTES_PER_MB)
    : 10;

  const handleFileSizeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const sizeMB = parseInt(e.target.value, 10) ?? 0;
    const sizeBytes = Math.min(sizeMB, MAX_FILE_SIZE_MB) * BYTES_PER_MB;
    onMaxFileSizeChange(sizeBytes);
  };

  const handleFileTypesChange = (values: string[]): void => {
    onAllowedFileTypesChange(values);
  };

  return (
    <div className='flex flex-col gap-2'>
      <CustomInput
        label='Maximum file size (MB)'
        type='number'
        value={maxFileSizeMB.toString()}
        onChange={handleFileSizeChange}
        placeholder='Enter max file size'
        className='bg-white'
        disabled={disabled}
      />

      <CustomSelect
        label='Allowed file types'
        value=''
        onValueChange={(value) => {
          if (value && !allowedFileTypes.includes(value)) {
            handleFileTypesChange([...allowedFileTypes, value]);
          }
        }}
        options={FILE_TYPE_OPTIONS.filter(
          (option) => !allowedFileTypes.includes(option.value)
        )}
        placeholder='Select file types to add'
        className='bg-white'
        disabled={disabled}
      />

      {allowedFileTypes.length > 0 && (
        <div className='space-y-2 mb-4'>
          <div className='text-sm font-medium'>Selected file types:</div>
          <div className='flex flex-wrap gap-2'>
            {allowedFileTypes.map((fileType) => {
              const option = FILE_TYPE_OPTIONS.find(
                (opt) => opt.value === fileType
              );
              return (
                <div
                  key={fileType}
                  className='flex items-center gap-1 bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm'
                >
                  <span>{option?.label ?? fileType}</span>
                  <button
                    type='button'
                    onClick={() =>
                      handleFileTypesChange(
                        allowedFileTypes.filter((type) => type !== fileType)
                      )
                    }
                    className='text-gray-500 hover:text-gray-700 text-lg ml-1'
                    disabled={disabled}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentUploadConfiguration;
